import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Statistic,
  Modal,
  Steps,
  Progress,
  Badge,
  Tooltip,
  Dropdown,
  Menu,
  List,
  Descriptions,
  Form,
  InputNumber,
  Checkbox,
  message,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  TruckOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  InboxOutlined,
  QrcodeOutlined,
  FileTextOutlined,
  WarningOutlined,
  MoreOutlined,
  ScanOutlined,
} from '@ant-design/icons';
import type { GoodsReceipt, PurchaseOrder, GoodsReceiptStatus, QualityCheckStatus } from '../../types';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Step } = Steps;

interface GoodsReceiptsProps {
  receipts: GoodsReceipt[];
  purchaseOrders: PurchaseOrder[];
  onReceiptCreate: (receipt: any) => void;
  onReceiptUpdate: (id: string, data: any) => void;
}

export const GoodsReceipts: React.FC<GoodsReceiptsProps> = ({
  receipts,
  purchaseOrders,
  onReceiptCreate,
  onReceiptUpdate,
}) => {
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<GoodsReceiptStatus | undefined>();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedReceipt, setSelectedReceipt] = useState<GoodsReceipt | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [qualityCheckForm] = Form.useForm();

  // Mock data for demonstration
  const mockReceipts: GoodsReceipt[] = [
    {
      id: '1',
      receiptNumber: 'GR-2024-001',
      purchaseOrderId: '1',
      vendorId: '1',
      receiptDate: new Date(),
      receivedBy: 'John Warehouse',
      warehouseId: 'WH001',
      status: 'quality_check',
      items: [
        {
          id: '1',
          purchaseOrderItemId: '1',
          productName: 'A4 Paper',
          orderedQuantity: 50,
          receivedQuantity: 50,
          acceptedQuantity: 48,
          rejectedQuantity: 2,
          unitOfMeasure: 'ream',
          batchNumber: 'BATCH-001',
          status: 'quality_check',
          rejectionReason: 'Minor damage to packaging',
        },
      ],
      totalQuantity: 50,
      notes: 'Shipment received in good condition, minor packaging damage on 2 units',
      qualityCheck: {
        required: true,
        status: 'in_progress',
        checkedBy: 'QC Team',
        checkedDate: new Date(),
        criteria: [
          {
            id: '1',
            name: 'Packaging Integrity',
            description: 'Check for damaged packaging',
            required: true,
            result: 'conditional',
            notes: '2 units with minor damage',
          },
          {
            id: '2',
            name: 'Quantity Verification',
            description: 'Verify received quantity matches order',
            required: true,
            result: 'pass',
          },
        ],
        overallResult: 'conditional',
        notes: 'Minor packaging damage noted',
      },
      attachments: [],
      createdAt: new Date(),
      updatedAt: new Date(),
    },
    {
      id: '2',
      receiptNumber: 'GR-2024-002',
      purchaseOrderId: '2',
      vendorId: '1',
      receiptDate: new Date(Date.now() - 24 * 60 * 60 * 1000),
      receivedBy: 'Sarah Logistics',
      warehouseId: 'WH001',
      status: 'accepted',
      items: [
        {
          id: '2',
          purchaseOrderItemId: '2',
          productName: 'Laptops',
          orderedQuantity: 10,
          receivedQuantity: 10,
          acceptedQuantity: 10,
          rejectedQuantity: 0,
          unitOfMeasure: 'each',
          serialNumbers: ['LT001', 'LT002', 'LT003', 'LT004', 'LT005'],
          status: 'accepted',
        },
      ],
      totalQuantity: 10,
      notes: 'All items received in excellent condition',
      qualityCheck: {
        required: true,
        status: 'passed',
        checkedBy: 'Mike QC',
        checkedDate: new Date(Date.now() - 12 * 60 * 60 * 1000),
        criteria: [
          {
            id: '1',
            name: 'Functional Test',
            description: 'Power on and basic functionality test',
            required: true,
            result: 'pass',
          },
          {
            id: '2',
            name: 'Physical Inspection',
            description: 'Check for physical damage',
            required: true,
            result: 'pass',
          },
        ],
        overallResult: 'pass',
      },
      attachments: [],
      createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000),
      updatedAt: new Date(Date.now() - 12 * 60 * 60 * 1000),
    },
  ];

  const [filteredData, setFilteredData] = useState(mockReceipts);

  useEffect(() => {
    let filtered = mockReceipts;

    if (searchText) {
      filtered = filtered.filter(receipt =>
        receipt.receiptNumber.toLowerCase().includes(searchText.toLowerCase()) ||
        receipt.receivedBy.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (statusFilter) {
      filtered = filtered.filter(receipt => receipt.status === statusFilter);
    }

    setFilteredData(filtered);
  }, [searchText, statusFilter]);

  const getStatusColor = (status: GoodsReceiptStatus): string => {
    switch (status) {
      case 'draft': return 'default';
      case 'received': return 'processing';
      case 'quality_check': return 'warning';
      case 'accepted': return 'success';
      case 'rejected': return 'error';
      case 'partially_accepted': return 'orange';
      default: return 'default';
    }
  };

  const getQualityCheckColor = (status: QualityCheckStatus): string => {
    switch (status) {
      case 'pending': return 'default';
      case 'in_progress': return 'processing';
      case 'passed': return 'success';
      case 'failed': return 'error';
      case 'conditional_pass': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: GoodsReceiptStatus) => {
    switch (status) {
      case 'received': return <TruckOutlined />;
      case 'quality_check': return <ScanOutlined />;
      case 'accepted': return <CheckCircleOutlined />;
      case 'rejected': return <ExclamationCircleOutlined />;
      case 'partially_accepted': return <WarningOutlined />;
      default: return <InboxOutlined />;
    }
  };

  const getReceiptProgress = (receipt: GoodsReceipt): number => {
    switch (receipt.status) {
      case 'draft': return 0;
      case 'received': return 25;
      case 'quality_check': return 50;
      case 'accepted': return 100;
      case 'rejected': return 100;
      case 'partially_accepted': return 75;
      default: return 0;
    }
  };

  const handleAction = (record: GoodsReceipt, action: string) => {
    switch (action) {
      case 'view':
        setSelectedReceipt(record);
        setIsModalVisible(true);
        break;
      case 'edit':
        onReceiptUpdate(record.id, record);
        break;
      case 'quality_check':
        // Show quality check modal
        Modal.info({
          title: 'Quality Check',
          width: 600,
          content: (
            <div>
              <Steps current={1} size="small" style={{ marginBottom: 16 }}>
                <Step title="Received" icon={<TruckOutlined />} />
                <Step title="Quality Check" icon={<ScanOutlined />} />
                <Step title="Completed" icon={<CheckCircleOutlined />} />
              </Steps>
              <Form layout="vertical">
                <Form.Item label="Quality Check Status">
                  <Select defaultValue={record.qualityCheck.status}>
                    <Option value="pending">Pending</Option>
                    <Option value="in_progress">In Progress</Option>
                    <Option value="passed">Passed</Option>
                    <Option value="failed">Failed</Option>
                    <Option value="conditional_pass">Conditional Pass</Option>
                  </Select>
                </Form.Item>
                <Form.Item label="Notes">
                  <Input.TextArea rows={3} defaultValue={record.qualityCheck.notes} />
                </Form.Item>
              </Form>
            </div>
          ),
        });
        break;
      case 'accept':
        message.success(`Goods receipt ${record.receiptNumber} accepted`);
        break;
      case 'reject':
        message.warning(`Goods receipt ${record.receiptNumber} rejected`);
        break;
    }
  };

  const actionMenu = (record: GoodsReceipt) => (
    <Menu>
      <Menu.Item key="view" icon={<EyeOutlined />} onClick={() => handleAction(record, 'view')}>
        View Details
      </Menu.Item>
      <Menu.Item key="edit" icon={<EditOutlined />} onClick={() => handleAction(record, 'edit')}>
        Edit
      </Menu.Item>
      {record.status === 'quality_check' && (
        <>
          <Menu.Item key="quality_check" icon={<ScanOutlined />} onClick={() => handleAction(record, 'quality_check')}>
            Quality Check
          </Menu.Item>
          <Menu.Item key="accept" icon={<CheckCircleOutlined />} onClick={() => handleAction(record, 'accept')}>
            Accept
          </Menu.Item>
          <Menu.Item key="reject" icon={<ExclamationCircleOutlined />} onClick={() => handleAction(record, 'reject')}>
            Reject
          </Menu.Item>
        </>
      )}
    </Menu>
  );

  const columns = [
    {
      title: 'Receipt Number',
      dataIndex: 'receiptNumber',
      key: 'receiptNumber',
      width: 150,
      fixed: 'left' as const,
      render: (text: string, record: GoodsReceipt) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ color: '#1890ff', cursor: 'pointer' }} onClick={() => handleAction(record, 'view')}>
            {text}
          </Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.receiptDate.toLocaleDateString()}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Purchase Order',
      dataIndex: 'purchaseOrderId',
      key: 'purchaseOrderId',
      width: 150,
      render: (poId: string) => (
        <Text style={{ color: '#1890ff' }}>PO-2024-{poId.padStart(3, '0')}</Text>
      ),
    },
    {
      title: 'Received By',
      dataIndex: 'receivedBy',
      key: 'receivedBy',
      width: 120,
    },
    {
      title: 'Warehouse',
      dataIndex: 'warehouseId',
      key: 'warehouseId',
      width: 100,
      render: (text: string) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: GoodsReceiptStatus) => (
        <Badge 
          status={getStatusColor(status) as any} 
          text={
            <Space>
              {getStatusIcon(status)}
              <span style={{ textTransform: 'capitalize' }}>
                {status.replace('_', ' ')}
              </span>
            </Space>
          } 
        />
      ),
    },
    {
      title: 'Total Quantity',
      dataIndex: 'totalQuantity',
      key: 'totalQuantity',
      width: 120,
      render: (quantity: number) => (
        <Text strong>{quantity}</Text>
      ),
    },
    {
      title: 'Quality Check',
      dataIndex: 'qualityCheck',
      key: 'qualityCheck',
      width: 140,
      render: (qc: any) => (
        <Space direction="vertical" size={0}>
          <Badge 
            status={getQualityCheckColor(qc.status) as any}
            text={qc.status.replace('_', ' ')}
          />
          {qc.checkedBy && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              by {qc.checkedBy}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Progress',
      key: 'progress',
      width: 120,
      render: (_, record: GoodsReceipt) => {
        const progress = getReceiptProgress(record);
        return (
          <Progress 
            percent={progress} 
            size="small" 
            status={record.status === 'rejected' ? 'exception' : 'normal'}
          />
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 60,
      fixed: 'right' as const,
      render: (_, record: GoodsReceipt) => (
        <Dropdown overlay={actionMenu(record)} trigger={['click']} placement="bottomRight">
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(selectedRowKeys);
    },
  };

  return (
    <div className="goods-receipts">
      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Receipts"
              value={filteredData.length}
              prefix={<InboxOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Quality Check"
              value={filteredData.filter(r => r.status === 'quality_check').length}
              prefix={<ScanOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Accepted"
              value={filteredData.filter(r => r.status === 'accepted').length}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Rejected"
              value={filteredData.filter(r => r.status === 'rejected').length}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Table Card */}
      <Card
        title={<Title level={4}>Goods Receipts</Title>}
        extra={
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => onReceiptCreate({})}>
              New Receipt
            </Button>
            <Button icon={<QrcodeOutlined />}>Scan QR Code</Button>
          </Space>
        }
      >
        {/* Filters */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Input
              placeholder="Search receipts..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col span={6}>
            <Select
              placeholder="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="draft">Draft</Option>
              <Option value="received">Received</Option>
              <Option value="quality_check">Quality Check</Option>
              <Option value="accepted">Accepted</Option>
              <Option value="rejected">Rejected</Option>
              <Option value="partially_accepted">Partially Accepted</Option>
            </Select>
          </Col>
          <Col span={10}>
            <RangePicker style={{ width: '100%' }} />
          </Col>
        </Row>

        {/* Bulk Actions */}
        {selectedRowKeys.length > 0 && (
          <div style={{ marginBottom: 16, padding: 8, background: '#f0f2f5', borderRadius: 4 }}>
            <Space>
              <Text>{selectedRowKeys.length} receipts selected</Text>
              <Button size="small">Bulk Accept</Button>
              <Button size="small">Quality Check</Button>
            </Space>
          </div>
        )}

        {/* Table */}
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          rowSelection={rowSelection}
          scroll={{ x: 1200 }}
          pagination={{
            total: filteredData.length,
            //pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: [10, 20, 50, 100, 500],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} receipts`,
          }}
        />
      </Card>

      {/* Receipt Details Modal */}
      <Modal
        title={`Goods Receipt - ${selectedReceipt?.receiptNumber}`}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        width={1000}
        footer={[
          <Button key="close" onClick={() => setIsModalVisible(false)}>
            Close
          </Button>,
          selectedReceipt?.status === 'quality_check' && (
            <Button key="accept" type="primary" onClick={() => selectedReceipt && handleAction(selectedReceipt, 'accept')}>
              Accept Receipt
            </Button>
          ),
        ]}
      >
        {selectedReceipt && (
          <div>
            <Steps current={getReceiptProgress(selectedReceipt) / 25} style={{ marginBottom: 24 }}>
              <Step title="Received" icon={<TruckOutlined />} />
              <Step title="Quality Check" icon={<ScanOutlined />} />
              <Step title="Accepted" icon={<CheckCircleOutlined />} />
            </Steps>

            <Descriptions column={2} bordered style={{ marginBottom: 24 }}>
              <Descriptions.Item label="Receipt Number">{selectedReceipt.receiptNumber}</Descriptions.Item>
              <Descriptions.Item label="Purchase Order">PO-2024-{selectedReceipt.purchaseOrderId.padStart(3, '0')}</Descriptions.Item>
              <Descriptions.Item label="Received By">{selectedReceipt.receivedBy}</Descriptions.Item>
              <Descriptions.Item label="Warehouse">{selectedReceipt.warehouseId}</Descriptions.Item>
              <Descriptions.Item label="Receipt Date">{selectedReceipt.receiptDate.toLocaleDateString()}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Badge status={getStatusColor(selectedReceipt.status) as any} text={selectedReceipt.status.replace('_', ' ')} />
              </Descriptions.Item>
              <Descriptions.Item label="Total Quantity" span={2}>{selectedReceipt.totalQuantity}</Descriptions.Item>
              <Descriptions.Item label="Notes" span={2}>{selectedReceipt.notes}</Descriptions.Item>
            </Descriptions>

            <Card title="Received Items" style={{ marginBottom: 24 }}>
              <Table
                dataSource={selectedReceipt.items}
                pagination={false}
                size="small"
                columns={[
                  { title: 'Product', dataIndex: 'productName', key: 'productName' },
                  { title: 'Ordered', dataIndex: 'orderedQuantity', key: 'orderedQuantity' },
                  { title: 'Received', dataIndex: 'receivedQuantity', key: 'receivedQuantity' },
                  { title: 'Accepted', dataIndex: 'acceptedQuantity', key: 'acceptedQuantity' },
                  { title: 'Rejected', dataIndex: 'rejectedQuantity', key: 'rejectedQuantity' },
                  { title: 'UOM', dataIndex: 'unitOfMeasure', key: 'unitOfMeasure' },
                  {
                    title: 'Status',
                    dataIndex: 'status',
                    key: 'status',
                    render: (status: string) => (
                      <Tag color={status === 'accepted' ? 'green' : status === 'rejected' ? 'red' : 'orange'}>
                        {status}
                      </Tag>
                    ),
                  },
                ]}
              />
            </Card>

            <Card title="Quality Check">
              <Descriptions column={2} bordered>
                <Descriptions.Item label="Required">{selectedReceipt.qualityCheck.required ? 'Yes' : 'No'}</Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Badge status={getQualityCheckColor(selectedReceipt.qualityCheck.status) as any} text={selectedReceipt.qualityCheck.status.replace('_', ' ')} />
                </Descriptions.Item>
                <Descriptions.Item label="Checked By">{selectedReceipt.qualityCheck.checkedBy}</Descriptions.Item>
                <Descriptions.Item label="Check Date">{selectedReceipt.qualityCheck.checkedDate?.toLocaleDateString()}</Descriptions.Item>
                <Descriptions.Item label="Overall Result" span={2}>
                  <Tag color={selectedReceipt.qualityCheck.overallResult === 'pass' ? 'green' : selectedReceipt.qualityCheck.overallResult === 'fail' ? 'red' : 'orange'}>
                    {selectedReceipt.qualityCheck.overallResult}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Notes" span={2}>{selectedReceipt.qualityCheck.notes}</Descriptions.Item>
              </Descriptions>
              
              <List
                style={{ marginTop: 16 }}
                header="Quality Check Criteria"
                dataSource={selectedReceipt.qualityCheck.criteria}
                renderItem={(criteria) => (
                  <List.Item>
                    <List.Item.Meta
                      title={
                        <Space>
                          {criteria.name}
                          <Tag color={criteria.result === 'pass' ? 'green' : criteria.result === 'fail' ? 'red' : 'orange'}>
                            {criteria.result}
                          </Tag>
                        </Space>
                      }
                      description={criteria.description}
                    />
                    {criteria.notes && <Text type="secondary">{criteria.notes}</Text>}
                  </List.Item>
                )}
              />
            </Card>
          </div>
        )}
      </Modal>
    </div>
  );
}; 